/**
 * ERP / Functional Area Consolidation — its own API client.
 *
 * ITS OWN, for the reason `portfolio_definition/api.ts:1-13` gives: products share no code
 * between their folders (`CLAUDE.md` §1.7, D-003), and this module alone would add a few
 * dozen methods to the platform's central `api` object that every other product would then
 * have to load. The request shape is the platform's (`src/api.ts`, and
 * `portfolio_definition/api.ts:27-45`): same-origin credentials, JSON in and out.
 *
 * WHAT IS DIFFERENT HERE, AND IT IS THE CONTRACT'S DOING. API_CONTRACT §1.6 fixes the
 * error envelope as `{ error: "<snake_case_code>", message?, …context }` and says plainly:
 * *"`error` is the contract; `message` is the copy. A UI switches on `error` and never
 * parses `message`."* So `ErpApiError` carries the CODE separately from the text, and keeps
 * the whole body — that is how `weights_do_not_balance` reaches a screen with its per-group
 * `groups[]` and §6.1's two verbatim sentences intact (§3.1).
 *
 * There is no `success` field on a 2xx (§1.2): the status is the signal, and the payload is
 * bare, keyed by resource.
 */

import {
  type AssessmentGridAreasPayload,
  type AssessmentGridCreate,
  type AssessmentGridDetailPayload,
  type AssessmentGridListPayload,
  type AssessmentGridClonePayload,
  type AssessmentUnitCreate,
  type AssessmentUnitCreatedPayload,
  type AssessmentUnitListPayload,
  type SystemListPayload,
  type AssessmentGridPayload,
  type AssessmentUnitPatch,
  type AssessmentUnitWritePayload,
  type AssessmentWritePayload,
  type BaselineInitiativePayload,
  type BusinessUnitListPayload,
  type BusinessUnitWrite,
  type CapabilityCreate,
  type CapabilityModelDeletePreview,
  type CapabilityModelEditPreview,
  type CapabilityModelListPayload,
  type CapabilityModelPatchedPayload,
  type CapabilityModelTreePayload,
  type CapabilityModelVersionCreatedPayload,
  type CapabilityPatch,
  type CapabilityAssessmentWrite,
  type CapabilityTemplateDetailPayload,
  type CapabilityTemplateLinesPayload,
  type CapabilityTemplateLineWrite,
  type CapabilityTemplateListPayload,
  type CapabilityTemplateWrite,
  type CapabilityTemplateWritePayload,
  type CapabilityWritePayload,
  type ErpContext,
  type InitiativeCreate,
  type InitiativeDetailPayload,
  type InitiativeListPayload,
  type InitiativePatch,
  type ScoringConfigPatch,
  type ScoringConfigPayload,
  type ScoringDocumentsPayload,
  type KnowledgeApplication,
  type KnowledgeFile,
  type KnowledgeFileDetail,
  type ScoringConfigWritePayload,
  type ScoresPayload,
  type UnitAssessmentsPayload,
  type UnitScorePayload,
  type WeightBalanceReport,
  type WeightChange,
} from './types';

const BASE = '/api/erp_consolidation';

/** The named error-context fields this module's screens actually read. */
export interface ErpErrorBody {
  error?: string;
  message?: string;
  /** §3.1, on `weights_do_not_balance`. */
  groups?: WeightBalanceReport['groups'];
  /** §1.8, on `role_not_permitted`. */
  required?: string[];
  held?: string[];
  /** §4.4 / §4.6, on `operating_mode_not_available`. */
  available?: string[];
  [field: string]: unknown;
}

export class ErpApiError extends Error {
  /** The machine-readable code. Branch on THIS, never on `message` (§1.6). */
  readonly code: string;
  readonly status: number;
  readonly body: ErpErrorBody;

  constructor(code: string, message: string, status: number, body: ErpErrorBody) {
    super(message);
    this.name = 'ErpApiError';
    this.code = code;
    this.status = status;
    this.body = body;
  }
}

/**
 * THE MOCK BOUNDARY, AND HOW IT LEAVES.
 *
 * The server for Phase 2 is being written in parallel, so the screens are built against
 * the contract and can be RUN against a mock of it. Two properties were required of that
 * mock: it must never ship, and removing it must be one edit.
 *
 * So it is a DEV-only dynamic import. `import.meta.env.DEV` is statically `false` in a
 * production build, which lets Vite drop this branch and with it the only reference to
 * `./mock/mockServer` — the module is not in the production bundle at all, rather than
 * being in it and unreachable. It is off unless somebody asks for it by name
 * (`?erpMock=1`, remembered for the tab), so a developer with a working server never meets
 * it either.
 *
 * TO DELETE IT when the real endpoints land: remove `mockFetch` and the `if` in
 * `request()` below, and delete `mock/`. Nothing else refers to either.
 */
const MOCK_FLAG = 'erpConsolidationMock';

function mockRequested(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    if (new URLSearchParams(window.location.search).get('erpMock') === '1') {
      window.sessionStorage.setItem(MOCK_FLAG, 'on');
    }
    return window.sessionStorage.getItem(MOCK_FLAG) === 'on';
  } catch {
    /* Private window, blocked site data: the mock is simply off. */
    return false;
  }
}

async function mockFetch(path: string, init: RequestInit | undefined): Promise<unknown> {
  const { handleMockRequest } = await import('./mock/mockServer');
  return handleMockRequest(path, init);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (mockRequested()) return (await mockFetch(path, init)) as T;

  const response = await fetch(`${BASE}${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    let body: ErpErrorBody = {};
    try {
      body = (await response.json()) as ErpErrorBody;
    } catch {
      /* Non-JSON body — the status line is all there is. */
    }
    const code = body.error ?? `http_${response.status}`;
    const message = body.message ?? body.error ?? `${response.status} ${response.statusText}`;
    throw new ErpApiError(code, message, response.status, body);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

const qs = (params: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
};

export const erpApi = {
  /** §4.2. Every Phase 2 screen calls this first. */
  context: (initiativeId?: string) =>
    get<{ context: ErpContext }>(`/context${qs({ initiativeId })}`),

  initiatives: {
    /** §4.3 — INI-01. */
    list: (params: { q?: string; status?: string; page?: number; limit?: number } = {}) =>
      get<InitiativeListPayload>(`/initiatives${qs(params)}`),
    /** §4.5 — INI-01. */
    get: (id: string) => get<InitiativeDetailPayload>(`/initiatives/${encodeURIComponent(id)}`),
    /** §4.4 — INI-02. */
    create: (body: InitiativeCreate) => post<InitiativeDetailPayload>('/initiatives', body),
    /** §4.6 — INI-03. */
    patch: (id: string, body: InitiativePatch) =>
      patch<InitiativeDetailPayload>(`/initiatives/${encodeURIComponent(id)}`, body),
    /** §4.7 — INI-03. 204, no body. */
    remove: (id: string) => del<void>(`/initiatives/${encodeURIComponent(id)}`),
    /** §4.8 — the initiative gate. SOL-03, §6.8. */
    baseline: (id: string) =>
      post<BaselineInitiativePayload>(`/initiatives/${encodeURIComponent(id)}/baseline`),
  },

  businessUnits: {
    /**
     * §5.1 — BU-01. A FLAT LIST, ordered by name; not nested and not paginated.
     *
     * No `activeOnly` and no parent pointer: D-022 dropped both columns (`0103`). The previous
     * version of this call took an `activeOnly` flag, and there is nothing left for it to filter —
     * *"All Business Units will be only active if they are defined"*.
     */
    list: () => get<BusinessUnitListPayload>('/business-units'),
    /** §5.2 — BU-02. */
    create: (body: BusinessUnitWrite) =>
      post<{ business_unit: BusinessUnitWrite & { id: string } }>('/business-units', body),
    /** §5.3 — BU-02. */
    patch: (id: string, body: Partial<BusinessUnitWrite>) =>
      patch<{ business_unit: BusinessUnitWrite & { id: string } }>(
        `/business-units/${encodeURIComponent(id)}`,
        body,
      ),
    /** §5.4 — BU-02. 204, no body; one guard refuses with a readable message. */
    remove: (id: string) => del<void>(`/business-units/${encodeURIComponent(id)}`),
  },

  capabilityModels: {
    /** §6.10 — the version list for one initiative, `version DESC`. */
    listForInitiative: (initiativeId: string) =>
      get<CapabilityModelListPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/capability-models`,
      ),
    /** §6.8 — CAP-01, CAP-02. Instantiating from a template COPIES; it does not share. */
    create: (
      initiativeId: string,
      body: {
        /** Required by §6.8 since `0105` — a model carries its own key. */
        model_key: string;
        name: string;
        description: string | null;
        from_capability_template_id?: string;
      },
    ) =>
      post<CapabilityModelTreePayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/capability-models`,
        body,
      ),
    /** §6.9 — CAP-01. The tree, flat with `parent_capability_id`, parents before children. */
    get: (id: string) =>
      get<CapabilityModelTreePayload>(`/capability-models/${encodeURIComponent(id)}`),
    /**
     * §6.11 — CAP-03. `name` and `description` only.
     *
     * A BASELINED MODEL IS NO LONGER REFUSED HERE (§6.19), which is what makes a version's name
     * correctable after the fact as well as settable at creation.
     */
    patch: (id: string, body: { name?: string; description?: string | null }) =>
      patch<CapabilityModelPatchedPayload>(`/capability-models/${encodeURIComponent(id)}`, body),
    /*
     * `baseline` IS GONE (`0106`), with its endpoint. Every capability model is in force from
     * creation — owner, 2026-10-07: *"All should be active and none should be draft. All should
     * be baselined"* — so there is no status left to move and no route to call.
     *
     * Its two real checks (not empty, every level totals exactly 100.0%) did NOT go with it:
     * they run when a model is assessed or scored against, in `scoring/model.ts`.
     */
    /**
     * §6.13 — CAP-03. Only a baselined model gets a new version.
     *
     * `name` is OPTIONAL on the wire and defaults to the source's — which is exactly why the screen
     * sends one: a version that is never named can only ever be called what its predecessor was.
     *
     * `description` is the same field one step further (owner, 2026-10-05), and its three cases are
     * NOT the same as `name`'s: text is used, `null` or `''` means this version has NO description,
     * and an ABSENT key falls back to the source's. The screen always sends it, so a description
     * left blank stays blank rather than inheriting a sentence written about the previous version.
     */
    newVersion: (id: string, body: { name?: string; description?: string | null } = {}) =>
      post<CapabilityModelVersionCreatedPayload>(
        `/capability-models/${encodeURIComponent(id)}/versions`,
        body,
      ),
    /**
     * §6.20 — delete a model nothing depends on. `204`, no body. HARD delete.
     *
     * `erp_administrator` ONLY, unlike every other write here: it is the one irreversible operation
     * on a capability model.
     */
    remove: (id: string) => del<void>(`/capability-models/${encodeURIComponent(id)}`),
    /** §6.14 — CAP-04. A new capability starts at 0.0%; sending a weight is refused. */
    addCapability: (modelId: string, body: CapabilityCreate) =>
      post<CapabilityWritePayload>(
        `/capability-models/${encodeURIComponent(modelId)}/capabilities`,
        body,
      ),
    /** §6.15 — CAP-04, CAP-05. `weight_locked` and `parent_capability_id` live here. */
    patchCapability: (modelId: string, capabilityId: string, body: CapabilityPatch) =>
      patch<CapabilityWritePayload>(
        `/capability-models/${encodeURIComponent(modelId)}/capabilities/${encodeURIComponent(capabilityId)}`,
        body,
      ),
    /** §6.16 — CAP-04. 200 with the tree, because the caller must see the new deficit. */
    removeCapability: (modelId: string, capabilityId: string) =>
      del<CapabilityWritePayload>(
        `/capability-models/${encodeURIComponent(modelId)}/capabilities/${encodeURIComponent(capabilityId)}`,
      ),
    /**
     * §6.17 — CAP-05. THE ONE WEIGHT WRITE, atomic across the set it is given.
     *
     * A full set is not required: capabilities not listed keep their weights, which is
     * why the editor can send one sibling group. An unbalanced set SAVES as draft and
     * comes back with the deficit in `weightBalance`.
     */
    putWeights: (modelId: string, weights: WeightChange[]) =>
      put<CapabilityWritePayload>(`/capability-models/${encodeURIComponent(modelId)}/weights`, {
        weights,
      }),

    /* ── THE PREVIEWS — `?dryRun=1`, §6.19 and §6.20 ───────────────────────────────────
     *
     * Same body, same validation, same warnings as the commit, and no write and no audit entry.
     * The preview is on the WRITE rather than on a GET of its own for D-015's reason: a second
     * code path computing the warning is a second implementation that can disagree with the one
     * that commits. So a screen that shows a confirmation before writing shows the SERVER's
     * sentence and the server's counts, and the dialog cannot word a change differently from the
     * audit trail.
     *
     * One method per endpoint rather than a `dryRun` flag on the five above: a preview and a
     * commit answer with different shapes (§6.19 — a preview carries NO `capabilities`), and a
     * flag that changed a return type would make every call site branch on it.
     */
    previewPatch: (id: string, body: { name?: string; description?: string | null }) =>
      patch<CapabilityModelEditPreview>(
        `/capability-models/${encodeURIComponent(id)}${qs({ dryRun: 1 })}`,
        body,
      ),
    previewAddCapability: (modelId: string, body: CapabilityCreate) =>
      post<CapabilityModelEditPreview>(
        `/capability-models/${encodeURIComponent(modelId)}/capabilities${qs({ dryRun: 1 })}`,
        body,
      ),
    previewPatchCapability: (modelId: string, capabilityId: string, body: CapabilityPatch) =>
      patch<CapabilityModelEditPreview>(
        `/capability-models/${encodeURIComponent(modelId)}/capabilities/${encodeURIComponent(capabilityId)}${qs({ dryRun: 1 })}`,
        body,
      ),
    previewRemoveCapability: (modelId: string, capabilityId: string) =>
      del<CapabilityModelEditPreview>(
        `/capability-models/${encodeURIComponent(modelId)}/capabilities/${encodeURIComponent(capabilityId)}${qs({ dryRun: 1 })}`,
      ),
    previewPutWeights: (modelId: string, weights: WeightChange[]) =>
      put<CapabilityModelEditPreview>(
        `/capability-models/${encodeURIComponent(modelId)}/weights${qs({ dryRun: 1 })}`,
        { weights },
      ),
    /**
     * §6.20's preview — "would this delete be refused, and why", from the same usage read the
     * commit uses. It is what lets the models table decide whether to offer the bin at all, and
     * say why where it does not, instead of rendering a control that fails on press.
     */
    previewDelete: (id: string) =>
      del<CapabilityModelDeletePreview>(
        `/capability-models/${encodeURIComponent(id)}${qs({ dryRun: 1 })}`,
      ),
  },

  /*
   * ── THE TEMPLATE LIBRARY — §6.1-§6.7 ─────────────────────────────────────────────────
   *
   * A template is a reusable capability structure — functional areas, capabilities and their
   * weights — that a new model can be created FROM, so an initiative starts with a weighted tree
   * instead of a blank page.
   *
   * IT IS THE MODEL'S ENDPOINTS AGAIN, ONE STEP EARLIER, and §6.7 says so in as many words:
   * *"Identical in shape and semantics to §6.17, over `capability_template_line`."* So these
   * methods mirror `capabilityModels` above field for field, including the two rules that matter:
   * a new line starts at 0.0% and sending a weight with it is refused (§6.6), and a weight is
   * settable only through `putWeights` (§6.7), so the 100% rule is checked in one place.
   *
   * THE ONE ASYMMETRY, and it is the server's own choice rather than something this client works
   * around: `putWeights` sends `capability_id` keys over a table whose rows are LINES.
   * `weights/write.ts:76-84` accepts either key deliberately — *"`capability_id` is what §6.17
   * specifies and `line_id` is the natural name over `capability_template_line` … Accepting both
   * costs one `??`"* — which is what lets the weight helpers in `@platform/erp-scoring` return one
   * `WeightChange[]` shape that both endpoints take, with no translation step that could drift.
   */
  capabilityTemplates: {
    /** §6.1. Also the list the New Capability Model panel offers; an unbalanced one cannot instantiate. */
    list: (params: { q?: string; page?: number; limit?: number } = {}) =>
      get<CapabilityTemplateListPayload>(`/capability-templates${qs(params)}`),
    /** §6.2 — one template and every one of its lines, flat with `parent_line_id`. */
    get: (id: string) =>
      get<CapabilityTemplateDetailPayload>(`/capability-templates/${encodeURIComponent(id)}`),
    /** §6.3 — `template_key` and `name` required. `409 template_key_in_use` names the clash. */
    create: (body: Partial<CapabilityTemplateWrite>) =>
      post<CapabilityTemplateDetailPayload>('/capability-templates', body),
    /**
     * §6.3a — CLONE a whole template, structure and weights included, in one transaction.
     * Needs its own `template_key` and `name`; the description defaults to the source's.
     */
    clone: (id: string, body: { template_key: string; name: string; description?: string | null }) =>
      post<CapabilityTemplateDetailPayload>(
        `/capability-templates/${encodeURIComponent(id)}/clone`,
        body,
      ),
    /**
     * §6.6a — re-order ONE sibling set. The whole set, in its new order; the server renumbers
     * `sort_order` 0..n-1 in a transaction and refuses anything that is not a permutation of
     * that parent's children, so a drag can reorder and never re-parent.
     */
    reorderLines: (id: string, parentLineId: string | null, lineIds: readonly string[]) =>
      put<CapabilityTemplateLinesPayload>(
        `/capability-templates/${encodeURIComponent(id)}/lines/order`,
        { parent_line_id: parentLineId, line_ids: lineIds },
      ),
    /** §6.4 — any subset of key, name and description. Same 409. */
    patch: (id: string, body: Partial<CapabilityTemplateWrite>) =>
      patch<CapabilityTemplateWritePayload>(
        `/capability-templates/${encodeURIComponent(id)}`,
        body,
      ),
    /**
     * §6.5 — 204, no body. Its lines cascade.
     *
     * REFUSED with `409 capability_template_in_use` where any model was created from it, because
     * a model's provenance is part of what its scores were computed against. The screen reads
     * `model_count` from the list and does not offer the control in that case; the server refuses
     * independently, which is the half that decides.
     */
    remove: (id: string) => del<void>(`/capability-templates/${encodeURIComponent(id)}`),
    /** §6.6 — a new line starts at 0.0%; sending a weight is refused outright. */
    addLine: (id: string, body: Partial<CapabilityTemplateLineWrite>) =>
      post<CapabilityTemplateLinesPayload>(
        `/capability-templates/${encodeURIComponent(id)}/lines`,
        body,
      ),
    /** §6.6 — `parent_line_id` lives here; a cycle is `400 capability_template_line_cycle`. */
    patchLine: (id: string, lineId: string, body: Partial<CapabilityTemplateLineWrite>) =>
      patch<CapabilityTemplateLinesPayload>(
        `/capability-templates/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
        body,
      ),
    /** §6.6 — 200 with every line, because the caller must see the new deficit. */
    removeLine: (id: string, lineId: string) =>
      del<CapabilityTemplateLinesPayload>(
        `/capability-templates/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
      ),
    /** §6.7 — THE ONE WEIGHT WRITE for a template. An unbalanced set saves; it cannot instantiate. */
    putWeights: (id: string, weights: WeightChange[]) =>
      put<CapabilityTemplateLinesPayload>(
        `/capability-templates/${encodeURIComponent(id)}/weights`,
        { weights },
      ),
  },

  /*
   * ── ASSESSMENT AND SCORING — §7b ──────────────────────────────────────────────────────
   *
   * EVERY ENDPOINT IN THIS GROUP, READ AND WRITE ALIKE, CAN ANSWER
   * `409 capability_model_not_baselined`. §6.1 keeps steps 4-9 locked until a model is
   * baselined, and §7b.0 puts one code behind three messages — no model at all / a draft that
   * does not balance / a draft that balances. A screen's branch is the same in all three: show
   * the server's `message`, which is the only thing that differs. So nothing here special-cases
   * them, and every screen that calls into this group renders that refusal as written.
   */
  assessmentGrid: {
    /**
     * §7b.3 — the areas the grid opens from. D-009's answer to the scale question.
     *
     * `capabilityModelId` since `0106`: with draft gone an initiative can hold several models
     * that are all in force, so the grid says which one it is about. Omitted, the server falls
     * back to the newest.
     */
    areas: (initiativeId: string, capabilityModelId?: string) =>
      get<AssessmentGridAreasPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-grid/areas` +
          qs(capabilityModelId ? { capabilityModelId } : {}),
      ),
    /**
     * §7b.4 — THE GRID, scoped to one functional area.
     *
     * `functionalAreaCapabilityId` is REQUIRED; omitting it is `400 field_required`, which names
     * §7b.3 as where to get one. Not paginated, for the reason §1.10 gives about the two trees:
     * a grid row whose columns are on another page cannot be rendered.
     */
    get: (
      initiativeId: string,
      params: {
        functionalAreaCapabilityId: string;
        businessUnitId?: string;
        /** `0106` — which of the initiative's models this grid is about. */
        capabilityModelId?: string;
        /**
         * `0107` — WHICH RUN. Without it the read matches every version of the scope at once
         * and interleaves them, with nothing on screen saying so.
         */
        assessmentGridId?: string;
      },
    ) =>
      get<AssessmentGridPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-grid${qs(params)}`,
      ),
  },

  assessments: {
    /** §7b.5 — one unit, one entry per capability in its area whether a cell exists or not. */
    forUnit: (initiativeId: string, assessmentUnitId: string) =>
      get<UnitAssessmentsPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units/${encodeURIComponent(assessmentUnitId)}/assessments`,
      ),
    /**
     * §7b.6 — **THE BULK CELL WRITE, AND THE PRIMARY FORM.** One transaction, and atomic:
     * nothing is written unless every cell passes every check, because *"a half-applied grid row
     * is worse than a refused one — it scores, and it scores from a state nobody chose."*
     *
     * Per-cell semantics are PATCH semantics: an absent key is untouched, an explicit `null`
     * clears a nullable column. So a grid sending only the field somebody typed must not blank
     * the confidence and the comment beside it — which is why `CapabilityAssessmentWrite` has
     * every field optional except `capability_id`.
     *
     * The response carries the RECOMPUTED score, so a grid never has to ask twice.
     */
    putForUnit: (
      initiativeId: string,
      assessmentUnitId: string,
      assessments: CapabilityAssessmentWrite[],
    ) =>
      put<AssessmentWritePayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units/${encodeURIComponent(assessmentUnitId)}/assessments`,
        { assessments },
      ),
    /**
     * §7b.7 — clears ONE cell. 204, no body.
     *
     * **Not the same statement as `is_not_applicable`:** N/A says the capability does not apply
     * and is scored as the configured option; a cleared cell says nothing was ever recorded and
     * the score treats it as `missing`. Both states have to be reachable or the grid cannot undo
     * a mistake.
     */
    clearCell: (initiativeId: string, assessmentUnitId: string, capabilityId: string) =>
      del<void>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units/${encodeURIComponent(assessmentUnitId)}/assessments/${encodeURIComponent(capabilityId)}`,
      ),
  },

  /**
   * SYSTEMS — what an initiative may select from.
   *
   * Read THROUGH the initiative, never from a table name: `systemSource.ts` decides whether that
   * means APR's applications or this module's own, from the initiative's operating mode. A screen
   * that reached past it would work in one mode and be wrong in the other.
   */
  systems: {
    /** §MODE-02 scoped to one initiative. `q` searches name and code; paged. */
    listForInitiative: (
      initiativeId: string,
      params: { q?: string; limit?: number; offset?: number } = {},
    ) =>
      get<SystemListPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/systems${qs(params)}`,
      ),
  },

  /**
   * ASSESSMENT GRIDS — one named, versioned RUN of an assessment (`0107`).
   *
   * The object a person saves, reopens and re-runs. Its scope is fixed at creation: changing
   * which model or area a saved run belongs to would re-column it and invalidate every cell,
   * so a different scope is a different grid.
   */
  assessmentGrids: {
    /** Every run of one initiative, grouped by scope, newest version of each first. */
    list: (initiativeId: string) =>
      get<AssessmentGridListPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-grids`,
      ),
    /**
     * EVERY run in the install. The Assessment Grid screen's single control: a run names its
     * own initiative, so making somebody pick one first asks a question the answer contains.
     */
    listAll: () => get<AssessmentGridListPayload>('/assessment-grids'),
    get: (assessmentGridId: string) =>
      get<AssessmentGridDetailPayload>(
        `/assessment-grids/${encodeURIComponent(assessmentGridId)}`,
      ),
    create: (initiativeId: string, body: AssessmentGridCreate) =>
      post<AssessmentGridDetailPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-grids`,
        body,
      ),
    /** Name and description only — the scope is not patchable. */
    patch: (assessmentGridId: string, body: { name?: string; description?: string | null }) =>
      patch<AssessmentGridDetailPayload>(
        `/assessment-grids/${encodeURIComponent(assessmentGridId)}`,
        body,
      ),
    /**
     * CLONE a run (`0108` — versions are gone). The systems come across; the recorded
     * percentages do NOT, because a copy that arrived looking already-assessed would be worse
     * than an empty one. The clone claims no lineage: either run can be renamed or deleted
     * without consequence for the other.
     */
    clone: (
      assessmentGridId: string,
      body: { name?: string; description?: string | null; copy_systems?: boolean } = {},
    ) =>
      post<AssessmentGridClonePayload>(
        `/assessment-grids/${encodeURIComponent(assessmentGridId)}/clone`,
        body,
      ),
    /** Refused if anything has been assessed in it. */
    remove: (assessmentGridId: string) =>
      del<void>(`/assessment-grids/${encodeURIComponent(assessmentGridId)}`),
  },

  assessmentUnits: {
    /** §7.9 — the units already selected, filterable by business unit and functional area. */
    list: (
      initiativeId: string,
      params: {
        businessUnitId?: string;
        functionalAreaCapabilityId?: string;
        limit?: number;
        offset?: number;
      } = {},
    ) =>
      get<AssessmentUnitListPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units${qs(params)}`,
      ),
    /**
     * SOL-01 — bulk selection, ATOMIC. Every triple is validated before anything is written, so
     * a batch either creates all of its units or none: a half-applied selection would leave the
     * user guessing which half.
     *
     * Named refusals worth handling: `business_unit_not_found`, `not_a_functional_area`,
     * `system_not_found`, `assessment_unit_exists`, and `initiative_is_baselined`.
     */
    create: (
      initiativeId: string,
      assessmentGridId: string,
      units: readonly AssessmentUnitCreate[],
    ) =>
      post<AssessmentUnitCreatedPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units`,
        { assessment_grid_id: assessmentGridId, units },
      ),
    /** Removing one unit. Refused once anything has been assessed against it. */
    remove: (initiativeId: string, assessmentUnitId: string) =>
      del<void>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units/${encodeURIComponent(assessmentUnitId)}`,
      ),
    /** §7b.8 — IMP-01. The two numbers §6.4 bands, plus the note. Stores nothing derived. */
    patch: (initiativeId: string, assessmentUnitId: string, body: AssessmentUnitPatch) =>
      patch<AssessmentUnitWritePayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units/${encodeURIComponent(assessmentUnitId)}`,
        body,
      ),
  },

  scores: {
    /** §7b.9 — SCO-02. Every unit scored, quadranted and ranked in §6.5's own order. */
    list: (
      initiativeId: string,
      params: {
        functionalAreaCapabilityId?: string;
        quadrant?: string;
        breakdown?: boolean;
      } = {},
    ) =>
      get<ScoresPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/scores${qs({
          functionalAreaCapabilityId: params.functionalAreaCapabilityId,
          quadrant: params.quadrant,
          /* `?breakdown=1`, which is the flag the server reads — `true` would not match. */
          breakdown: params.breakdown ? 1 : undefined,
        })}`,
      ),
    /**
     * §7b.10 — SCO-03. **THE BREAKDOWN**, and it always carries one.
     *
     * *"A breakdown rebuilt in the browser is a second implementation of §6 — and one that can
     * agree with the total while disagreeing about why."* So the screen that explains a score
     * renders this and computes nothing.
     */
    forUnit: (initiativeId: string, assessmentUnitId: string) =>
      get<UnitScorePayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/assessment-units/${encodeURIComponent(assessmentUnitId)}/score`,
      ),
  },

  scoringConfig: {
    /** §7b.1 — SCO-01. An absent row resolves to the defaults and says `source: 'default'`. */
    get: (initiativeId: string) =>
      get<ScoringConfigPayload>(`/initiatives/${encodeURIComponent(initiativeId)}/scoring-config`),
    /**
     * §7b.2 — SCO-01. Upsert with PATCH semantics, and `erp_administrator` only.
     *
     * `dryRun` IS THE WHOLE POINT OF THE SCREEN. D-015: the definition never freezes, so
     * changing it on a baselined initiative must put the consequence in front of the person who
     * can judge it. The preview runs every line of the handler except the write and the audit —
     * same validation, same proposed row, same warning — so the dialog cannot disagree with the
     * write, *"because it IS the write, stopped one line short of it."*
     */
    put: (initiativeId: string, body: ScoringConfigPatch, options: { dryRun?: boolean } = {}) =>
      put<ScoringConfigWritePayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/scoring-config${qs({
          dryRun: options.dryRun ? 1 : undefined,
        })}`,
        body,
      ),
  },

  /**
   * D-025 — the Knowledge Library: APR's Architecture & Process Diagrams documents, one shared
   * library. The calls mirror APR's `compat/aiKnowledgeApi.ts` one for one (list, upload, get,
   * update, delete, download) against this module's own routes, which hold every call to that one
   * category.
   */
  knowledge: {
    list: () => get<KnowledgeFile[]>('/knowledge/files'),
    applications: () => get<KnowledgeApplication[]>('/knowledge/applications'),
    get: (id: string) => get<KnowledgeFileDetail>(`/knowledge/files/${encodeURIComponent(id)}`),
    /** Multipart: the browser sets the boundary, so the JSON content type must NOT be sent. */
    upload: (form: FormData) =>
      request<KnowledgeFile[]>('/knowledge/files', { method: 'POST', body: form, headers: {} }),
    update: (
      id: string,
      body: { description?: string; tags?: string[]; application_id?: string | null },
    ) => patch<KnowledgeFile>(`/knowledge/files/${encodeURIComponent(id)}`, body),
    remove: (id: string) =>
      del<{ message: string }>(`/knowledge/files/${encodeURIComponent(id)}`),
    /** The original bytes, and the filename the server's Content-Disposition names. */
    download: async (id: string): Promise<{ blob: Blob; filename: string }> => {
      const response = await fetch(
        `${BASE}/knowledge/files/${encodeURIComponent(id)}/download`,
        { credentials: 'same-origin' },
      );
      if (!response.ok) {
        throw new ErpApiError(
          `http_${response.status}`,
          `${response.status} ${response.statusText}`,
          response.status,
          {},
        );
      }
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const match = /filename="([^"]+)"/.exec(disposition);
      return {
        blob: await response.blob(),
        filename: match?.[1] ? decodeURIComponent(match[1]) : 'download',
      };
    },
  },

  /**
   * D-024 — documents from APR's Knowledge Library → Architecture & Process Diagrams, LINKED to
   * an initiative's scoring definition. Nothing is uploaded here and nothing is written to APR:
   * every call returns the whole picture (attached + still available) so the screen re-renders
   * from one answer.
   */
  scoringDocuments: {
    list: (initiativeId: string) =>
      get<ScoringDocumentsPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/scoring-documents`,
      ),
    attach: (initiativeId: string, documentId: string, isEvidence = false) =>
      post<ScoringDocumentsPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/scoring-documents`,
        { document_id: documentId, is_evidence: isEvidence },
      ),
    setEvidence: (initiativeId: string, documentId: string, isEvidence: boolean) =>
      patch<ScoringDocumentsPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/scoring-documents/${encodeURIComponent(documentId)}`,
        { is_evidence: isEvidence },
      ),
    detach: (initiativeId: string, documentId: string) =>
      del<ScoringDocumentsPayload>(
        `/initiatives/${encodeURIComponent(initiativeId)}/scoring-documents/${encodeURIComponent(documentId)}`,
      ),
    /** A plain URL — the browser downloads it with the session cookie, as APR's library does. */
    downloadUrl: (initiativeId: string, documentId: string) =>
      `${BASE}/initiatives/${encodeURIComponent(initiativeId)}/scoring-documents/${encodeURIComponent(documentId)}/download`,
  },
};
